import { chainData } from "../chains";

/**
 * Resolve a `chainData` row from the loose chain identifiers the UI has to
 * work with.
 *
 * The marketplace spread a single `chainIndex` prop across consumers that pass
 * genuinely different things:
 *   - `src/containers/Collection` passes `cDat.network` — a chain NAME
 *     ("Flare", "Anvil", "Avalanche"), never an array index.
 *   - `src/components/NftDetailModal` passes an array index.
 *   - `src/routes/$locale/item/...` passes an EVM chain id.
 *
 * All three appear on the same token, so every consumer resolves its own row
 * and they must agree. Matching is tried widest-first (exact index, then the
 * name fields, then a numeric chain id) so a numeric-looking network name can
 * never be mistaken for a chain id.
 *
 * Pure — no hooks, no I/O. Safe to call during render and during SSR.
 */
export function resolveChainRow(input?: {
  chainIndex?: number | string | null;
  chainId?: number | string | null;
}): any | undefined {
  const rows: any[] = (chainData as any[]) || [];
  if (!rows.length) return undefined;

  const ci = input?.chainIndex;
  if (ci !== undefined && ci !== null && ci !== '') {
    // Form 1: an actual array index into chainData.
    if (typeof ci === 'number' && rows[ci]) return rows[ci];

    // Form 2: a chain NAME. `network` repeats across rows ("Flare" covers
    // Coston 1, Coston 2 and Songbird), so try the distinguishing fields
    // before the generic one.
    const needle = String(ci).toLowerCase();
    const byChainName = rows.find((r) => r && String(r.chainName).toLowerCase() === needle);
    if (byChainName) return byChainName;
    const byTokName = rows.find((r) => r && String(r.tokName).toLowerCase() === needle);
    if (byTokName) return byTokName;
    const byNetwork = rows.find((r) => r && String(r.network).toLowerCase() === needle);
    if (byNetwork) return byNetwork;

    // Form 3: a numeric string that is a valid EVM chain id.
    const asNum = Number(ci);
    if (Number.isFinite(asNum)) {
      const byId = rows.find((r) => r && parseInt(String(r.chainId), 10) === asNum);
      if (byId) return byId;
    }
  }

  if (input?.chainId !== undefined && input?.chainId !== null && input?.chainId !== '') {
    const byId = rows.find((r) => r && parseInt(String(r.chainId), 10) === Number(input.chainId));
    if (byId) return byId;
  }

  return undefined;
}

/** EVM chain id for a resolved row, as a number. Undefined when unresolvable. */
export function chainRowToChainId(row: any): number | undefined {
  if (!row || row.chainId === undefined || row.chainId === null) return undefined;
  const n = parseInt(String(row.chainId), 10);
  return Number.isFinite(n) ? n : undefined;
}
